import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { MoleImageCarouselModal } from '../skin/components';
import { getImageUrl, getFallbackImageUrl, formatMoleDate, getBodyPartName } from '../skin/utils';

/**
 * Public view component for displaying skin moles in read-only mode
 * Used in shared access links with separate backend serializers
 * @param {Object} props - Component props
 * @param {Array} props.molesData - Array of user moles from shared data (from backend serializer)
 * @param {Array} props.moleImagesData - Array of all mole images from shared data (optional, may be nested)
 * @param {Object} props.bodyPartsData - Body parts data (optional, for translations)
 */
const PublicSkinMolesView = ({ 
  molesData = [], 
  moleImagesData = [],
  bodyPartsData = {},
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Modal state for image carousel
  const [showCarouselModal, setShowCarouselModal] = useState(false);
  const [selectedMole, setSelectedMole] = useState(null);
  const [selectedMoleImages, setSelectedMoleImages] = useState([]);
  const [selectedMoleBodyPartName, setSelectedMoleBodyPartName] = useState(null);

  // Process moles data - handle different possible structures from backend serializer
  const userMoles = useMemo(() => {
    if (!molesData) {
      return [];
    }
    
    // If it's already an array, use it directly
    if (Array.isArray(molesData)) {
      return molesData;
    }
    
    // If it's an object with results array
    if (molesData.results && Array.isArray(molesData.results)) {
      return molesData.results;
    }
    
    // If it's an object with user_moles array
    if (molesData.user_moles && Array.isArray(molesData.user_moles)) {
      return molesData.user_moles;
    }
    
    return [];
  }, [molesData]);

  // Process images data - handle different possible structures
  // Backend serializer may include images nested in moles or as separate array
  const allMoleImages = useMemo(() => {
    const images = [];
    
    // First, check if images are provided as separate array
    if (moleImagesData && Array.isArray(moleImagesData)) {
      images.push(...moleImagesData);
    } else if (moleImagesData?.results && Array.isArray(moleImagesData.results)) {
      images.push(...moleImagesData.results);
    }
    
    // Also check if images are nested in moles (backend serializer may include them)
    userMoles.forEach((mole) => {
      if (Array.isArray(mole.images)) {
        images.push(...mole.images);
      }
      if (Array.isArray(mole.user_mole?.images)) {
        images.push(...mole.user_mole.images);
      }
      if (Array.isArray(mole.mole_images)) {
        images.push(...mole.mole_images);
      }
    });
    
    return images;
  }, [moleImagesData, userMoles]);

  /**
   * Helper to extract mole UUID from image object
   * Handles different possible structures from backend serializer
   * @param {Object} image - Image object
   * @returns {string|null} - Mole UUID or null if not found
   */
  const getMoleUuidFromImage = (image) => {
    if (image.user_mole) {
      if (typeof image.user_mole === 'object') {
        return image.user_mole.uuid || image.user_mole.id;
      }
      if (typeof image.user_mole === 'string') {
        return image.user_mole;
      }
    }
    return image.mole_uuid || image.user_mole_uuid || image.mole_id || 
           (image.mole?.uuid || image.mole?.id) || null;
  };

  // Group images by mole UUID
  // Handle different possible structures from backend serializer
  const imagesByMole = useMemo(() => {
    const grouped = {};
    
    allMoleImages.forEach((image) => {
      const moleUuid = getMoleUuidFromImage(image);
      
      if (moleUuid) {
        const uuidKey = String(moleUuid);
        grouped[uuidKey] = grouped[uuidKey] || [];
        grouped[uuidKey].push(image);
      }
    });
    
    return grouped;
  }, [allMoleImages]);

  /**
   * Helper to get translated body part name
   * @param {string} bodyPartCode - Body part code
   * @returns {string|null} - Translated body part name or null
   */
  const getDisplayBodyPartName = (bodyPartCode) => {
    if (!bodyPartCode || bodyPartCode === 'None' || bodyPartCode === null) {
      return null;
    }
    return getBodyPartName(bodyPartCode, bodyPartsData, false, currentLanguage);
  };

  // Modal handlers
  const handleShowCarousel = (mole, images) => {
    const translatedBodyPartName = getDisplayBodyPartName(mole.body_part);
    setSelectedMole(mole);
    setSelectedMoleImages(images);
    setSelectedMoleBodyPartName(translatedBodyPartName);
    setShowCarouselModal(true);
  };

  const handleHideCarousel = () => {
    setShowCarouselModal(false);
    setSelectedMole(null);
    setSelectedMoleImages([]);
    setSelectedMoleBodyPartName(null);
  };

  /**
   * Helper to get Bootstrap badge color variant based on risk level
   * @param {string} badge - Risk level badge (LOW, MEDIUM, HIGH)
   * @returns {string} - Bootstrap badge color variant
   */
  const getMoleBadgeColor = (badge) => {
    const colorMap = {
      LOW: 'success',
      MEDIUM: 'warning',
      HIGH: 'danger',
    };
    return colorMap[badge] || 'secondary';
  };

  /**
   * Helper to get mole risk level badge
   * @param {Object} mole - Mole object
   * @returns {string|null} - Risk level badge or null
   */
  const getMoleBadge = (mole) => {
    return mole.risk_level || mole.riskLevel || mole.classification || mole.mole_badge || null;
  };

  /**
   * Helper to get mole UUID from mole object
   * Handles different possible structures from backend serializer
   * @param {Object} mole - Mole object
   * @returns {string|null} - Mole UUID or null if not found
   */
  const getMoleUuid = (mole) => {
    return mole.uuid || mole.id || 
           (mole.user_mole?.uuid || mole.user_mole?.id) || null;
  };

  /**
   * Helper to get images for a specific mole
   * Checks both grouped images and directly nested images in mole object
   * @param {Object} mole - Mole object
   * @param {string|null} moleUuid - Mole UUID
   * @returns {Array} - Array of mole images
   */
  const getMoleImages = (mole, moleUuid) => {
    // First try to get images from grouped images
    let moleImages = moleUuid ? (imagesByMole[String(moleUuid)] || []) : [];
    
    // If no images found in grouped, check if images are directly nested in mole
    if (moleImages.length === 0) {
      if (Array.isArray(mole.images)) {
        moleImages = mole.images;
      } else if (Array.isArray(mole.mole_images)) {
        moleImages = mole.mole_images;
      } else if (Array.isArray(mole.user_mole?.images)) {
        moleImages = mole.user_mole.images;
      }
    }
    
    return moleImages;
  };

  if (userMoles.length === 0) {
    return (
      <div className='text-center py-4'>
        <p className='text-muted mb-0'>{t('shared-access.public-view.no-data-available')}</p>
      </div>
    );
  }

  return (
    <>
      <Row>
        {userMoles.map((mole) => {
          const moleUuid = getMoleUuid(mole);
          const moleImages = getMoleImages(mole, moleUuid);
          const sortedImages = [...moleImages].sort((a, b) => new Date(b.created) - new Date(a.created));
          
          // Use first/reference image for thumbnail
          const referenceImage =
            moleImages.find((img) => img.uuid === mole.reference_image) || sortedImages[0];

          // Get the best available image URL for thumbnail
          const imageUrl = referenceImage
            ? getImageUrl(referenceImage, false)
            : getFallbackImageUrl(mole);

          const moleBadge = getMoleBadge(mole);
          const bodyPartName = getDisplayBodyPartName(mole.body_part);

          return (
            <Col xs={12} md={6} lg={4} key={moleUuid || mole.mole_code} className='mb-4'>
              <Card className='h-100 shadow-sm'>
                <Card.Body className='p-0'>
                  {/* Image at the top - full width */}
                  <div className='mole-image-container position-relative' style={{ width: '100%' }}>
                    <img
                      src={imageUrl}
                      alt={mole.name || mole.description || 'Mole'}
                      className='mole-image'
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        borderTopLeftRadius: '0.375rem',
                        borderTopRightRadius: '0.375rem',
                        cursor: sortedImages.length > 0 ? 'pointer' : 'default',
                      }}
                      onClick={() =>
                        sortedImages.length > 0 && handleShowCarousel(mole, sortedImages)
                      }
                      onError={(e) => {
                        e.target.src = '/api/placeholder/150/150';
                      }}
                    />

                    {/* Image counter badge */}
                    {sortedImages.length > 1 && (
                      <Badge
                        bg='primary'
                        className='position-absolute top-0 start-0 m-1'
                        style={{ fontSize: '0.7rem' }}
                      >
                        📸 {sortedImages.length}
                      </Badge>
                    )}

                    {/* Reference image indicator */}
                    {referenceImage?.uuid === mole.reference_image && (
                      <Badge
                        bg='success'
                        className='position-absolute top-0 end-0 m-1'
                        style={{ fontSize: '0.6rem' }}
                      >
                        📌 REF
                      </Badge>
                    )}

                    {/* View images button overlay */}
                    {sortedImages.length > 0 && (
                      <div
                        className='position-absolute bottom-0 start-0 end-0 p-2'
                        style={{
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                          borderBottomLeftRadius: '0.375rem',
                          borderBottomRightRadius: '0.375rem',
                        }}
                      >
                        <button
                          className='btn btn-light btn-sm w-100'
                          style={{ fontSize: '0.7rem', opacity: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowCarousel(mole, sortedImages);
                          }}
                        >
                          🔍 {t('skinMoles.moleCard.viewImage')}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Info below image */}
                  <div className='p-3 mole-info'>
                    {/* Mole title and code */}
                    <div className='mb-2'>
                      <div className='d-flex align-items-center gap-2 mb-1 flex-wrap'>
                        <h6 className='mb-0'>{mole.name || t('skinMoles.moleCard.unnamedMole')}</h6>
                        {moleBadge && (
                          <div className='d-flex align-items-center gap-1'>
                            <small className='text-muted' style={{ fontSize: '0.75rem' }}>
                              {t('skinMoles.moleCard.riskLevel')}:
                            </small>
                            <Badge bg={getMoleBadgeColor(moleBadge)} className='fs-6'>
                              {t(`skinMoles.moleBadge.${moleBadge}`) || moleBadge}
                            </Badge>
                          </div>
                        )}
                      </div>
                      <span className='mole-code text-muted' style={{ fontSize: '0.75rem' }}>
                        {mole.mole_code}
                      </span>
                      {bodyPartName && (
                        <div className='mt-1'>
                          <small className='text-muted'>
                            📍 {bodyPartName}
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {mole.description && (
                      <div className='mb-2'>
                        <small className='text-muted'>{mole.description}</small>
                      </div>
                    )}

                    {/* Distinctive features */}
                    {mole.distinctive_features && (
                      <div className='mb-2'>
                        <small className='features-text'>
                          {t('skinMoles.moleCard.features')} {mole.distinctive_features}
                        </small>
                      </div>
                    )}

                    {/* AI Description */}
                    {mole.ai_description && (
                      <div className='mb-3'>
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <i className='bi bi-robot' style={{ fontSize: '1.1rem', color: '#0d6efd' }}></i>
                          <strong className='text-primary'>{t('skinMoles.moleCard.aiDescription')}</strong>
                        </div>
                        <div
                          className='p-3 rounded border'
                          style={{
                            fontSize: '0.875rem',
                            backgroundColor: '#e7f3ff',
                            borderColor: '#0d6efd !important',
                            borderWidth: '2px',
                            boxShadow: '0 2px 4px rgba(13, 110, 253, 0.1)',
                          }}
                        >
                          <Markdown>{mole.ai_description}</Markdown>
                        </div>
                      </div>
                    )}

                    {/* Image count and size info */}
                    <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-2'>
                      <small className='text-muted mb-1 mb-md-0'>
                        {sortedImages.length === 1
                          ? t('skinMoles.moleCard.imageCount').replace('{{count}}', sortedImages.length)
                          : sortedImages.length >= 5
                            ? t('skinMoles.moleCard.imageCountPlural2').replace(
                                '{{count}}',
                                sortedImages.length,
                              )
                            : t('skinMoles.moleCard.imageCountPlural').replace(
                                '{{count}}',
                                sortedImages.length,
                              )}
                      </small>
                      {mole.approximate_size_mm && (
                        <small className='text-muted'>📏 {mole.approximate_size_mm}mm</small>
                      )}
                    </div>

                    {/* Creation date */}
                    {mole.created && (
                      <div className='mt-2'>
                        <small className='text-muted d-block mb-1'>
                          {t('skinMoles.moleCard.moleCreated')}{' '}
                          {new Date(mole.created).toLocaleDateString()}
                        </small>
                        {sortedImages.length > 1 && sortedImages[0]?.created && (
                          <small className='text-primary d-block'>
                            {t('skinMoles.moleCard.latestImage')}{' '}
                            {formatMoleDate(sortedImages[0].created)}
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Image Carousel Modal - Read-only mode */}
      {selectedMole && (
        <MoleImageCarouselModal
          show={showCarouselModal}
          onHide={handleHideCarousel}
          mole={selectedMole}
          moleImages={selectedMoleImages}
          translatedBodyPartName={selectedMoleBodyPartName}
          readOnly={true}
        />
      )}
    </>
  );
};

export default PublicSkinMolesView;
